/* eslint-disable functional/no-let */
import { DataType, IColumnType, ITableState, Order, RowFormatType } from './types';
import { ObjectView } from 'components/ObjectView';
import { isValidElement } from 'react';

function parseDateString(dateString: string): Date {
  const [day, month, year, hour, minute, period] = dateString.split(/[\s:.]+/);
  let hour24 = parseInt(hour, 10);
  if (period === 'PM' && hour24 !== 12) {
    hour24 += 12;
  } else if (period === 'AM' && hour24 === 12) {
    hour24 = 0;
  }
  return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10), hour24, parseInt(minute, 10));
}

function descendingComparator<Key extends keyof any>(
  a: { [key in Key]: number | string | Date },
  b: { [key in Key]: number | string | Date },
  orderBy: Key
) {
  if (typeof a[orderBy] === 'string' && /\d{2}\.\d{2}\.\d{4}\s\d{2}:\d{2}\s[APM]{2}/.test(a[orderBy] as string)) {
    const dateA = parseDateString(a[orderBy] as string);
    const dateB = parseDateString(b[orderBy] as string);
    return dateB.getTime() - dateA.getTime();
  }
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

export function getComparator<Key extends keyof any>(
  order: 'asc' | 'desc',
  orderBy: Key
): (a: { [key in Key]: number | string | Date }, b: { [key in Key]: number | string | Date }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export const handleRequestSort = (tableState: ITableState, setState: any, property: string) => {
  const isAsc = tableState.sortBy === property && tableState.orderBy === 'asc';
  setState({ ...tableState, orderBy: isAsc ? 'desc' : 'asc', sortBy: property });
};

export const formatValue = (type: DataType, value: any, col: IColumnType, record: Record<string, number>) => {
  switch (type) {
    case 'link':
      return 'link';
    case 'custom':
      if (col.customValueHandle) {
        return col.customValueHandle(value, record);
      }
      return value;
    case 'json':
      // eslint-disable-next-line functional/no-try-statement
      try {
        return { value, object: ObjectView({ jsonString: value }) };
      } catch (error) {
        //console.error(error);
        return value;
      }
    default:
      return value || '';
  }
};

export const limitLength = (value: string | any, maxLength?: number): string => {
  if (isValidElement(value.object)) return limitLength(value.value, maxLength);
  if (!maxLength) return value;
  if (value.length < maxLength) return value;
  return String(value).substring(0, maxLength) + '...';
};

export const formatActiveCell = <T extends RowFormatType>(row: T) => {
  const _isActive = !!row?.active;
  const _inTransfer = !!row?.siteToTransferTo;
  const p = 1;
  const borderRadius = 0.5;
  const color = _inTransfer ? '936100' : _isActive ? '#04AE79' : '#FF6969';
  const backgroundColor = _inTransfer ? '#F9F1D1' : _isActive ? '#e2ece8' : '#f1d6d7';
  const display = 'flex';
  const justifyContent = 'center';
  return { p, borderRadius, color, backgroundColor, display, justifyContent };
};
